import type {
  Assignment,
  BuildPlatform,
  Course,
  CourseOrganization,
  Entity,
  EntityDict,
  LinkedModuleBuilds,
  LinkedPlayerBuilds,
  LinkedPlayerBuildsListItem,
  Platform,
  RunResult,
  Survey,
  SurveyAnswers,
  User,
  UserAssignmentStatus,
  UserCoursePerformance,
  UserSlim
} from '@webapp2/shared/model'
import { reactive } from 'vue'

export const state = reactive({
  adminPermissions: new Set<AdminPermission>(),
  adminTechniques: <EntityDict<PlayerTechnique>>{},
  assignments: <EntityDict<Assignment>>{},
  linkedModuleBuilds: <EntityDict<LinkedModuleBuilds>>{},
  linkedPlayerBuilds: <EntityDict<LinkedPlayerBuilds>>{},
  courses: <EntityDict<Course>>{},
  courseOrganizations: <EntityDict<CourseOrganization>>{},
  featureSwitches: new Set<string>(),
  latestPlayerBuildSubmittedToMeta: <LinkedPlayerBuildsListItem | undefined>undefined,
  multiplayerSessions: <EntityDict<MultiplayerSession>>{},
  navToggle: <boolean | undefined>undefined,
  organizationId: <string | undefined>undefined,
  organizationPermissions: <{ [orgId: string]: Set<OrgPermission> }>{},
  organizations: <EntityDict<Organization>>{},
  permissionsAccepted: true,
  permissionStatus: <PermissionStatus | undefined>undefined,
  permissionGroups: <EntityDict<PermissionGroup>>{},
  platform: <Platform>'web',
  playerTechniques: <EntityDict<PlayerTechnique>>{},
  prebriefs: <EntityDict<Prebrief>>{},
  runResults: <EntityDict<RunResult>>{},
  registeredStates: <EntityDict<ModuleBuildRegistrationState>>{},
  survey: <Survey | undefined>undefined,
  surveyAnswers: <{ [runId: string]: SurveyAnswers }>{},
  user: <User | undefined>undefined,
  userAssignmentStatuses: <EntityDict<UserAssignmentStatus>>{},
  userCoursePerformances: <EntityDict<UserCoursePerformance>>{},
  users: <EntityDict<UserSlim>>{},
  vuplex: <VuplexData | undefined>undefined
})

export type State = typeof state

export function etagId(type: string) {
  return `Etag_${type}`
}

export type Permission = OrgPermission | AdminPermission

export const orgPermissionTypes = ['ElevatedAccess'] as const
export type OrgPermission = (typeof orgPermissionTypes)[number]

export const adminPermissionTypes = [
  'Admin.BasicAccess',
  'Courses.Edit',
  'Organizations.AllPermissions',
  'Organizations.Switch.All',
  'PlayerTechniques.Edit',
  'PlayerBuilds.Edit',
  'Runs.View',
  'Users',
  'Users.PermissionGroups.Edit'
] as const
export type AdminPermission = (typeof adminPermissionTypes)[number]

export type PermissionGroup = Entity & {
  name: string
  type: 'PermissionGroup'
  isOrganizationLevel: boolean
  permissions: Permission[]
}

export type Organization = Entity & {
  name: string
  isNurseTraining: boolean
  healthStreamOrganizationId?: string
}

export type VuplexData = {
  deviceId?: string
  qrReaderAvailable: boolean
  passThroughEnabled: boolean
  startInQrMode: boolean
  runId?: string
  courseId?: string
  linkedPlayerBuildsId?: string
  playerBuildId?: string
  platform: BuildPlatform
  features?: { [id: string]: boolean }
}

export type QrErrorCode = 'Invalid' | 'Used' | 'Expired'

export type PermissionStatus = 'granted' | 'settingsOpened' | 'denied' | 'deniedAndDontAskAgain' | 'blockedByPolicy'

export type PlayerTechnique = Entity & {
  name: string
  tileImageUri: string
  heroImageUri: string
  durationMinutes: number
  targetLearner: string
  role: string
  isTutorial: boolean
  categories: string[]
  type: 'playerTechnique'
}

export function prebriefId(techniqueId: string) {
  return `Prebrief_${techniqueId}`
}

export type Prebrief = Entity & {
  learningObjectives: LearningObjectives
  scenarioDetails: ScenarioDetails
  type: 'prebrief'
}

export type LearningObjectives = {
  objectives: string[]
  preparationMaterials: Link[]
  needsAssessmentUrl?: string
}

export type Link = {
  label: string
  url: string
}

export type ScenarioDetails = {
  situation: string
  background: string
  assessment: string
  recommendation: string
  importantNote: string | undefined
}

export type BuildDownloadState = 'NotRegistered' | 'Queued' | 'Downloading' | 'Downloaded' | 'Error'

export type BuildDownloadStatus = {
  id: string
  downloadedBytes: number
  totalBytes: number
  state: BuildDownloadState
}

export const multiplayerSessionStatuses = [
  'Scheduled',
  'Starting',
  'Running',
  'Finished',
  'Cancelled',
  'Error'
] as const
export type MultiplayerSessionStatus = (typeof multiplayerSessionStatuses)[number]

export type MultiplayerSession = Entity & {
  inviteCode: string
  courseId?: string
  techniqueId: string
  userId: string
  date: string
  address: string
  contentReady: boolean
  status: MultiplayerSessionStatus
  moduleId?: string
  organizationId: string
  serverBuildId: string
  linkedModuleBuildsId: string
  invitedIds: string[]
  error?: string
}

export function registeredStateId(techniquePath: string) {
  return `RegisteredState_${techniquePath}`
}

export type ModuleBuildRegistrationState = Entity & {
  techniquePath: string
  linkedModuleBuildsId: string
  type: 'RegisteredState'
  downloadStatus: BuildDownloadStatus
}

export function getCookie(name: string) {
  const cookies = document.cookie.split('; ')
  for (const cookie of cookies) {
    const [key, value] = cookie.split('=')
    if (key === name) {
      return decodeURIComponent(value!)
    }
  }
  return null
}

export function setCookie(name: string, value: string, expiry?: Date) {
  const expiryValue = expiry ? `;expires=${expiry.toUTCString()}` : ''
  document.cookie = `${name}=${value}${expiryValue};path=/`
}

export function deleteCookie(name: string) {
  setCookie(name, '', new Date(0))
}

export function isString(x: unknown): x is string {
  return typeof x === 'string'
}
